// @divorceos/engine — California guideline / spousal support calculation engine (web rebuild of Xspouse's CAXeng.dll).
// Algorithm source: /home/dev/xspouse-re/notes/CAXeng.md. All Case money is monthly; tax interfaces are annual.
import type { Case, CaseResult, SettlementTable, TimesharePosition } from '@divorceos/schema';
import { computeCase as computeCaseImpl } from './compute.js';
import { settlementTable as settlementTableImpl, timesharePositions as timesharePositionsImpl } from './reports.js';
import type { EngineOptions } from './provider.js';

/** Full recalculation of a case (guideline, proposed, taxes, cash flow, per child). */
export function computeCase(c: Case, opts?: EngineOptions): CaseResult { return computeCaseImpl(c, opts); }
/** Settlement Possibilities table for tactics 2..8. */
export function settlementTable(c: Case, releases: number | null, opts?: EngineOptions): SettlementTable { return settlementTableImpl(c, releases, opts); }
/** Support at each timeshare percentage 0..100 (all children, or one child). */
export function timesharePositions(c: Case, childIndex: number | null, opts?: EngineOptions): TimesharePosition[] { return timesharePositionsImpl(c, childIndex, opts); }

export type { EngineOptions, EngineCompat, TaxProvider, W4Facts } from './provider.js';
export { defaultTaxProvider, publishedTablesProvider, taxProviderFor, providerOf, DEFAULT_COMPAT } from './provider.js';
export * from './constants.js';
export { normalizeCase as normalizeEngineCase, autoExemptions, ageAtYearEnd, isAge65, partyLabelSet } from './normalize.js';
export type { NCase, NParty, NChild, NExemptions } from './normalize.js';
export {
  buildFederalInput, buildCaInput, computeReturn, applyStateTaxAdjustment, applyFederalTaxAdjustment, qbidWorksheetFor, federalWithQbidWorksheet, scenarioExemptions, TaxContext, annualToMonthly, monthlyToAnnual, ssFederallyTaxable, prorationIncome, NO_SUPPORT,
} from './taxes.js';
export type { TaxOutcome, ScenarioSpec, PassSpec, ReturnQbidWorksheet } from './taxes.js';
export { computeQbid, qbidThreshold, QBID_THRESHOLDS, QBID_PHASE_IN_RANGE, QBID_RATE } from './qbid.js';
export type { QbidContext, QbidResult, QbidBusinessResult } from './qbid.js';
export { netDisposable, grossIncome, guidelineDeductions } from './net.js';
export type { PartyNets } from './net.js';
export {
  kFraction, kFactor, basicCs, hPercent, countySsFormula, userSsFormula, formulaSs, supportFromNets, allocateAddOns,
  lowIncomeThreshold, lowIncomeAdjustment, lowIncomeAdjustmentSb343, sb343LowIncomeThreshold, kFractionSb343, resolveFc4055Version,
  effectiveCceAllocation, guidelinePass, computeGuideline, signedBy, payorOf, paidBy, receivedBy,
} from './guideline.js';
export type { GuidelineOutcome, GuidelineOptions, SupportFromNets, SsFormulaArgs, Fc4055Resolved } from './guideline.js';
export { computeProposed, resolveReleases, resolveChildReleases, TacticSolver, tacticTarget } from './proposed.js';
export type { ProposedOutcome, ChildReleases } from './proposed.js';
export { partyScenario, withholding } from './cashflow.js';
export { settlementStep, guidelineFor } from './reports.js';
export { paymentDescription, toLine } from './compute.js';

// ---- bonus income module (implemented in bonus.ts)
export { bonusTable, caseWithBonus, uniformBonusTax, variableBonusTax, interpolateAllocation, bonusStepSchedule, bonusTableCap, BONUS_TABLE_MAX_AMOUNT } from './bonus.js';
export type { BonusAllocation } from './bonus.js';
